import React, { useCallback, useRef, useState } from 'react';
import { FileIcon } from '../../components/attachments/FileIcon';
import { formatFileSize } from '../../components/attachments/fileIconUtils';
import { Button } from '../../components/ui';
import {
  EyeIcon,
  PlusIcon,
  SpinnerIcon,
  TrashIcon,
  UploadCloudIcon,
} from '../../components/ui/Icons';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import api from '../../services/api';
import { eFormFileDownloadPath, uploadEFormFiles } from '../../services/mutations';
import type { EFormFieldSchema, EFormFileMeta } from './schema';
import { isEFormFileMeta } from './schema';

function normalizeValue(value: unknown, multiple: boolean): EFormFileMeta[] {
  if (multiple) {
    return Array.isArray(value) ? value.filter(isEFormFileMeta) : [];
  }
  return isEFormFileMeta(value) ? [value] : [];
}

async function openEFormFile(storageKey: string) {
  const response = await api.get(eFormFileDownloadPath(storageKey), { responseType: 'blob' });
  const raw = response.data as Blob;
  const ct =
    (response.headers['content-type'] as string | undefined) ||
    raw.type ||
    'application/octet-stream';
  const blob = raw.type ? raw : new Blob([raw], { type: ct });
  const blobUrl = window.URL.createObjectURL(blob);
  const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
  if (win) {
    const revoke = () => window.URL.revokeObjectURL(blobUrl);
    win.addEventListener('beforeunload', revoke, { once: true });
    window.setTimeout(revoke, 120_000);
  } else {
    window.URL.revokeObjectURL(blobUrl);
  }
}

export const EFormFileField: React.FC<{
  field: EFormFieldSchema;
  value: unknown;
  onChange: (value: EFormFileMeta | EFormFileMeta[] | null) => void;
  readOnly?: boolean;
  error?: string;
}> = ({ field, value, onChange, readOnly = false, error }) => {
  const { colors } = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewingKey, setViewingKey] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const multiple = Boolean(field.multiple);
  const files = normalizeValue(value, multiple);
  const totalCount = files.length;
  const accept = field.accept?.trim() || '*/*';
  const hint = field.helpText;

  const commit = useCallback(
    (next: EFormFileMeta[]) => {
      if (multiple) onChange(next);
      else onChange(next[0] ?? null);
    },
    [multiple, onChange],
  );

  const uploadPicked = useCallback(
    async (picked: File[]) => {
      if (!picked.length || readOnly) return;
      setLocalError(null);
      setUploading(true);
      try {
        const uploaded = await uploadEFormFiles(picked);
        if (multiple) {
          commit([...files, ...uploaded]);
        } else {
          commit(uploaded.slice(0, 1));
        }
      } catch (e: any) {
        setLocalError(e?.response?.data?.message || e?.message || 'Upload failed');
      } finally {
        setUploading(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [commit, files, multiple, readOnly],
  );

  const openFilePicker = () => inputRef.current?.click();

  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    void uploadPicked(Array.from(e.target.files || []));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (!readOnly && !uploading && e.dataTransfer.types.includes('Files')) setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (readOnly || uploading) return;
    const dropped = Array.from(e.dataTransfer.files || []);
    void uploadPicked(multiple ? dropped : dropped.slice(0, 1));
  };

  const removeAt = (index: number) => {
    if (readOnly) return;
    commit(files.filter((_, i) => i !== index));
  };

  const viewFile = useCallback(async (storageKey: string) => {
    setLocalError(null);
    setViewingKey(storageKey);
    try {
      await openEFormFile(storageKey);
    } catch (e: any) {
      setLocalError(e?.response?.data?.message || e?.message || 'Could not open file');
    } finally {
      setViewingKey(null);
    }
  }, []);

  const showError = error || localError;

  return (
    <div
      className="space-y-3"
      onDragEnter={readOnly || uploading ? undefined : handleDragEnter}
      onDragOver={readOnly || uploading ? undefined : handleDragOver}
      onDragLeave={readOnly || uploading ? undefined : handleDragLeave}
      onDrop={readOnly || uploading ? undefined : handleDrop}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {totalCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {totalCount} file{totalCount !== 1 ? 's' : ''}
              </span>
            )}
            {uploading && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                <SpinnerIcon className="h-3 w-3" />
                Uploading…
              </span>
            )}
          </div>
          {hint ? (
            <p className={cn('mt-1 text-xs leading-relaxed', colors.textMuted)}>{hint}</p>
          ) : null}
        </div>
        {!readOnly && totalCount > 0 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openFilePicker}
            disabled={uploading}
            className="shrink-0"
          >
            <UploadCloudIcon className="mr-1.5 h-4 w-4" />
            {multiple ? 'Add files' : 'Replace file'}
          </Button>
        ) : null}
      </div>

      {!readOnly ? (
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          onChange={handleAddFiles}
          className="hidden"
          accept={accept}
        />
      ) : null}

      {totalCount === 0 && readOnly ? (
        <div
          className={cn(
            'rounded-xl border border-dashed px-5 py-8 text-center',
            colors.border,
            colors.surfaceHover,
          )}
        >
          <p className={cn('text-sm font-medium', colors.text)}>No files</p>
          <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
            Uploaded files for this field will appear here
          </p>
        </div>
      ) : totalCount === 0 && !readOnly ? (
        <button
          type="button"
          onClick={openFilePicker}
          disabled={uploading}
          className={cn(
            'flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors',
            colors.border,
            colors.surfaceHover,
            colors.interactiveHover,
            showError && 'border-red-400',
            dragActive &&
              'border-indigo-400 bg-indigo-50/50 ring-2 ring-indigo-500/40 dark:border-indigo-500 dark:bg-indigo-950/30',
            uploading && 'opacity-70',
          )}
        >
          <div
            className={cn(
              'mb-3 flex h-12 w-12 items-center justify-center rounded-full',
              dragActive
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                : colors.info,
            )}
          >
            {uploading ? (
              <SpinnerIcon className="h-6 w-6" />
            ) : (
              <UploadCloudIcon className="h-6 w-6" />
            )}
          </div>
          <span className={cn('text-sm font-medium', colors.text)}>
            {uploading
              ? 'Uploading…'
              : dragActive
                ? 'Release to attach files'
                : multiple
                  ? 'No files yet'
                  : 'No file yet'}
          </span>
          <span className={cn('mt-1 max-w-sm text-xs leading-relaxed', colors.textMuted)}>
            Click here or drag {multiple ? 'files' : 'a file'} into this area.
          </span>
        </button>
      ) : (
        <div
          className={cn(
            'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3',
            dragActive &&
              !readOnly &&
              'rounded-xl ring-2 ring-indigo-500/50 ring-offset-2 ring-offset-transparent',
          )}
        >
          {files.map((f, index) => {
            const isViewing = viewingKey === f.storageKey;
            return (
              <article
                key={`${f.storageKey}-${index}`}
                className={cn(
                  'group relative flex min-w-0 flex-col rounded-xl border p-3 shadow-sm transition-shadow',
                  colors.border,
                  colors.surface,
                  'hover:shadow-md',
                )}
              >
                <div className="flex items-start gap-3">
                  <FileIcon fileName={f.fileName} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className={cn('truncate text-sm font-medium', colors.text)} title={f.fileName}>
                        {f.fileName}
                      </p>
                      <span className="shrink-0 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                        Saved
                      </span>
                    </div>
                    {f.size != null ? (
                      <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
                        {formatFileSize(f.size)}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-end gap-1 border-t pt-2 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isViewing}
                    onClick={() => void viewFile(f.storageKey)}
                    className="h-8 px-2"
                  >
                    {isViewing ? <SpinnerIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    <span className="ml-1.5 hidden sm:inline">View</span>
                  </Button>
                  {!readOnly ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAt(index)}
                      className={cn('h-8 px-2', colors.error)}
                    >
                      <TrashIcon className="h-4 w-4" />
                      <span className="ml-1.5 hidden sm:inline">Remove</span>
                    </Button>
                  ) : null}
                </div>
              </article>
            );
          })}
          {!readOnly && multiple ? (
            <button
              type="button"
              onClick={openFilePicker}
              disabled={uploading}
              className={cn(
                'flex min-h-[7.5rem] flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-5 text-center transition-colors',
                colors.border,
                colors.surfaceHover,
                colors.interactiveHover,
                dragActive &&
                  'border-indigo-400 bg-indigo-50/50 dark:border-indigo-500 dark:bg-indigo-950/30',
              )}
            >
              <div
                className={cn(
                  'mb-2 flex h-10 w-10 items-center justify-center rounded-full',
                  dragActive
                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                    : colors.info,
                )}
              >
                {dragActive ? (
                  <UploadCloudIcon className="h-5 w-5" />
                ) : (
                  <PlusIcon className="h-5 w-5" />
                )}
              </div>
              <span className={cn('text-sm font-medium', colors.text)}>
                {dragActive ? 'Drop files here' : 'Add another file'}
              </span>
              <span className={cn('mt-0.5 text-xs', colors.textMuted)}>Click or drag files</span>
            </button>
          ) : null}
        </div>
      )}

      {showError ? <p className="text-xs text-red-600">{showError}</p> : null}
    </div>
  );
};

export { openEFormFile };
